/* eslint-disable react/no-danger */
/* @flow */
import React from 'react';
import { Map, List } from 'immutable';
import classnames from 'classnames/bind';
import memoize from 'lodash.memoize';

import s from './ReportContent.styl';
const cx = classnames.bind(s);
import { ErrorBoundary } from './ErrorBoundary.js';

import {
  getReportIndicators,
  parseAndSanitizeJsonToHtml,
  matchIOCsRegular,
  matchIOCsJSON
} from 'trustar/reports/extraction';

const highlightIOCsJSON = memoize(
  (report: Map<any, any>) => {
    const json = parseAndSanitizeJsonToHtml(report.getIn(['json', 'parsed']));
    const indicators = getReportIndicators(report.get('indicators', List()));
    return matchIOCsJSON(json, indicators);
  },
  x => x.get('reportBody') /* Cache key */
);

const highlightIOCs = memoize((text, indicators) => {
  return matchIOCsRegular(text, indicators, (word, i) => (
    <span key={i}>{word}</span>
  ));
});

/**
 * This component is responsible for rendering report content. However, rather
 * than rendering a string we also do a bit of highlighting and decide how
 * exactly to render the content based on whether it's json, whether it's huge,
 * or whether it's just a standard report.
 *
 * NOTE: The 20kb or larger equals large report measurement is fairly arbitrary,
 * but it should catch most of the cases we've seen in the past.
 */
export class ReportContent extends React.Component {
  props: {
    report: Map<any, any>,
    reportMetadata?: Map<any, any>,
    expanded?: boolean
  };

  /**
   * A simple wrapper around highlightIOCs
   */
  highlight = (str: string = '') => {
    const { report } = this.props;
    const indicators = getReportIndicators(report.get('indicators', List()));
    return highlightIOCs(str, indicators);
  };

  renderFallback = () => {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '1.67rem',
          paddingTop: '3.33rem'
        }}>
        <h1>
          <i className='fa fa-exclamation-circle'></i>
        </h1>
        <p>This report could not be displayed.</p>
      </div>
    );
  };

  render() {
    const { report, expanded = true, reportMetadata = Map() } = this.props;
    const body = expanded
      ? report.get('reportBody', '')
      : reportMetadata.get('reportBodySnippet', '');
    let content;
    const isProcessed = report.get('submissionStatus') === 'PROCESSED';

    if (!isProcessed) {
      content = (
        <pre
          style={{
            background: 'transparent',
            border: 'none',
            paddingLeft: '1.25rem'
          }}>
          Report is still being processed...
        </pre>
      );
    } else if (typeof body !== 'string' && isProcessed) {
      // An error processing the report
      content = (
        <pre
          style={{
            background: 'transparent',
            border: 'none',
            paddingLeft: '1.25rem'
          }}>
          Error fetching report content.
        </pre>
      );
    } else if (typeof body !== 'string') {
      // Nothing to render yet...
      content = (
        <pre
          style={{
            background: 'transparent',
            border: 'none',
            paddingLeft: '1.25rem'
          }}>
          Processing Report Content...
        </pre>
      );
    } else if (report.getIn(['json', 'valid'])) {
      // Render JSON body
      const renderableJSON = highlightIOCsJSON(report);
      content = (
        <div
          className={cx('reportJSON')}
          dangerouslySetInnerHTML={{ __html: renderableJSON }}
        />
      );
    } else {
      // Render standard report
      const len = body.length;
      if (len > 20000) {
        console.warn(
          'Rendering large report. UI slowness is likely to occur...'
        );
        console.warn(`Report size: ${len / 1000}kb`);
      }
      content = (
        <div className={cx('reportContent')}>
          <pre>{this.highlight(body)}</pre>
        </div>
      );
    }

    return (
      <ErrorBoundary renderFallback={this.renderFallback}>
        {content}
      </ErrorBoundary>
    );
  }
}



// WEBPACK FOOTER //
// ./web_modules/trustar/components/ReportContent.js